/**
 * Готовые вопросы аналитика Astra → инструменты канонического шлюза.
 *
 * Прототип считал ответ у себя: `astra/recipes.ts` сворачивал журнал
 * обязательств прямо в браузере. На канонических объёмах это не «медленнее» —
 * это неверно: журнал приходит страницами, и свёртка неполной выборки
 * выглядела бы полным итогом. Поэтому вопрос остаётся тем же, а считает его
 * СЕРВЕР: рецепт превращается в вызов инструмента (`/ai/answer`), а ответ
 * печатает общий рендерер конверта (`features/ai/AnswerSpecView`).
 *
 * Таблица ниже — единственное место соответствия. Она объявлена данными, а не
 * условиями внутри экрана, ровно чтобы её можно было проверить тестом: шесть
 * вопросов Astra обязаны иметь исполнителя в каноне, иначе экран предлагает
 * человеку вопрос, на который никто не ответит.
 *
 * ПОЧЕМУ У ОДНОГО ВОПРОСА ДВА ИНСТРУМЕНТА. «Как идут сезонные работы» — это
 * ДВЕ оси (отчитались и проверено), и канон считает их раздельно. Склеивать
 * их в один вызов значило бы завести третью правду рядом с двумя
 * существующими; экран показывает два ответа подряд и называет каждый.
 */

import type { LevelId } from '../../../lib/levels';
import type { RecipeId } from '../../astra/model';
import { defineMessages } from '../../../i18n/define';
import { tr } from '../../../i18n/runtime';

const m = defineMessages({
  reported: {
    id: 'data.analyst.note.reported',
    defaultMessage: 'Отчёт принимается как факт; отметки проверяющего видны в разборе работы, но долей «проверено» ответ не считает.',
    description: 'Оговорка под ответом ИИ-аналитика на вопрос про отчёты (доля сданных отчётов): как читать цифру. Показывается рядом с ответом',
  },
  water: {
    id: 'data.analyst.note.water',
    defaultMessage: 'Баланс воды считает сервер по заявкам и подачам из базы района. Объём не превращается в политую площадь.',
    description: 'Оговорка под ответом ИИ-аналитика на вопрос про воду: что считает сервер и чего число не значит',
  },
  attention: {
    id: 'data.analyst.note.attention',
    defaultMessage: 'Очередь внимания — не рейтинг территорий: это список мест, где данные требуют проверки.',
    description: 'Оговорка под ответом ИИ-аналитика на вопрос «где внимание»: список — не рейтинг',
  },
  timeliness: {
    id: 'data.analyst.note.timeliness',
    defaultMessage: 'Знаменатель — полученные отчёты, а не все обязательства: своевременность отчётности не доказывает выполнение в поле.',
    description: 'Оговорка под ответом ИИ-аналитика на вопрос про своевременность отчётов: что взято за знаменатель',
  },
  season: {
    id: 'data.analyst.note.season',
    defaultMessage: 'Сравнение описательное: состав контуров между сезонами меняется, и это не причинный эффект.',
    description: 'Оговорка под ответом ИИ-аналитика на вопрос про сравнение с прошлым сезоном',
  },
  source: {
    id: 'data.analyst.note.source',
    defaultMessage: 'Состав среза и происхождение записей — ответ о данных, а не о поле.',
    description: 'Оговорка под ответом ИИ-аналитика на вопрос про источники данных',
  },
});


export interface RecipeCall {
  tool_id: string;
  /** Уровень ЭКРАНА, с которого задан вопрос. Полномочием не является. */
  level: LevelId;
  /** Параметры вызова в терминах записи реестра инструмента. */
  params: Record<string, string | number>;
}

export interface RecipeBinding {
  /** Один вопрос — один или несколько вызовов; порядок и есть порядок ответа. */
  calls: readonly RecipeCall[];
  /**
   * Чем этот вопрос отличается от прототипа. Печатается на экране рядом с
   * ответом: смена источника меняет и знаменатель, и молчать об этом нельзя.
   */
  note: string;
}

/**
 * Горизонт по умолчанию. Вопросы Astra — сезонные: «как идут сезонные работы»
 * с недельным горизонтом отвечал бы про другую популяцию, чем спрашивали.
 */
const SEASON = { horizon: 'season' } as const;

export const RECIPE_TOOLS: Record<RecipeId, RecipeBinding> = {
  'M-CAM-02': {
    calls: [
      { tool_id: 'M-WORK-reported', level: 'district', params: { ...SEASON } },
    ],
    get note() {
      return tr(m.reported);
    },
  },
  'R-X01': {
    calls: [{ tool_id: 'R-WAT-balance', level: 'district', params: {} }],
    get note() {
      return tr(m.water);
    },
  },
  'R-X04': {
    calls: [{ tool_id: 'R-ATTN-queue', level: 'district', params: { ...SEASON } }],
    get note() {
      return tr(m.attention);
    },
  },
  'M-CAM-05': {
    calls: [{ tool_id: 'M-WORK-timeliness', level: 'district', params: {} }],
    get note() {
      return tr(m.timeliness);
    },
  },
  'R-X05': {
    calls: [{ tool_id: 'R-SEASON-same-stage', level: 'district', params: {} }],
    get note() {
      return tr(m.season);
    },
  },
  'R-X07': {
    calls: [{ tool_id: 'R-SOURCE-ref', level: 'district', params: {} }],
    get note() {
      return tr(m.source);
    },
  },
};

/** Инструменты, на которые опирается хотя бы один вопрос экрана. */
export function boundToolIds(): string[] {
  const seen = new Set<string>();
  for (const binding of Object.values(RECIPE_TOOLS)) {
    for (const call of binding.calls) seen.add(call.tool_id);
  }
  return [...seen];
}

/**
 * Ключевые слова свободного вопроса → готовые разборы.
 *
 * Здесь НЕ делается попытка «понять» вопрос: разбор свободного вопроса —
 * работа сервера (`/ai/plan`). Эта подсказка нужна ровно для случая, когда
 * сервер отказался планировать: вместо пустого экрана человек видит, какие
 * готовые вопросы ближе всего к его словам.
 */
// i18n-ignore: разбор русских слов вопроса; для узбекского вопроса сервер планирует сам
export function nearestRecipes(question: string): RecipeId[] {
  const text = question.toLowerCase();
  if (/вод|полив|подач/.test(text)) return ['R-X01'];
  if (/прошл|сравн|истори|измен/.test(text)) return ['R-X05'];
  if (/проверк|источник|достовер/.test(text)) return ['R-X07'];
  if (/территор|внимани|массив/.test(text)) return ['R-X04'];
  if (/отч|работ|срок/.test(text)) return ['M-CAM-02', 'M-CAM-05'];
  return [];
}
